import { useEffect, useState, useCallback } from 'react';
import { Plus, Package, Edit, Trash2, AlertTriangle, X, Save } from 'lucide-react';
import { api } from '../../api/client';
import type { LicensePackage, LicenseModule, ModuleCode } from '../../types/domain';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Uputstvo, Pojam } from '../../components/Uputstvo';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

export function LicensePackagesPage() {
  const [packages, setPackages] = useState<LicensePackage[]>([]);
  const [modules, setModules] = useState<LicenseModule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<LicensePackage | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LicensePackage | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        api.get<LicensePackage[]>('/admin/license-packages'),
        api.get<LicenseModule[]>('/admin/license-packages/modules'),
      ]);
      setPackages(p);
      setModules(m);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/license-packages/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Licencni paketi</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Paket je <strong>šablon</strong>, ne licenca - prepisuje se na klijenta u trenutku dodele. Javni paket sa cenom je i <strong>stavka cenovnika</strong> (sajt, samouslužni katalog).
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}><Plus size={14} /> Novi paket</button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <Uputstvo id="licence" naslov="Kako rade licence - pročitaj pre prve dodele">
        <Pojam naziv="Modul">
          Najmanja jedinica koja se licencira (<code>POS_BASIC</code>, <code>MAGACIN</code>,
          <code> EFAKTURE</code>…). Spisak je u kodu - nov modul znači izmenu programa, ne unos ovde.
        </Pojam>
        <Pojam naziv="Paket">
          Šablon za prodaju: spisak modula + dve podrazumevane vrednosti (mesta i trajanje).
          Paket sam po sebi nikome ništa ne daje.
        </Pojam>
        <Pojam naziv="Licenca">
          Pravi zapis kod klijenta - <strong>jedan red po modulu</strong>, sa svojim brojem mesta i rokom.
          Nastaje kad se paket dodeli ili kad se modul doda ručno.
        </Pojam>
        <Pojam naziv="Mesta">
          Koliko <strong>uređaja</strong> sme istovremeno da bude prijavljeno. Kad se prijavi još jedan,
          najstarija kasa (ona koja se najduže nije javila) se odjavljuje u roku od pet minuta i dobija
          poruku šta se desilo. Mesto preko prvog se i naplaćuje, po ceni iz cenovnika.
        </Pojam>
        <Pojam naziv="Trajanje">
          Broj dana od dodele do <code>važi do</code>. Posle isteka ide <strong>7 dana grejsa</strong> u
          kom sve radi normalno; posle toga klijent <strong>vidi svoje podatke, ali ne izdaje dokumente</strong>.
          Suspenzija naloga je nešto drugo - tada ne radi ništa osim prijave, podrške i naplate.
        </Pojam>

        <div style={naglaseno}>
          <strong>Podrazumevane vrednosti se prepisuju, ne prate.</strong> Kad dodeliš paket, njegova
          mesta i trajanje se <em>kopiraju</em> u novonastale licence. Kasnija izmena paketa ne dira
          nijednog zatečenog klijenta - isto pravilo kao kod cene: ko je kupio pod starim uslovima,
          pod njima i ostaje. Zato je ovde reč „podrazumevano", a ne „važeće".
        </div>

        <div style={naglaseno}>
          <strong>Licence su knjiga, ne stanje.</strong> Obnova ne menja postojeći red nego pravi nov,
          a stari ide u <code>replaced</code>. Zato klijent ume da ima više redova za isti modul -
          merodavan je uvek onaj koji važi <strong>najduže</strong>. Ništa se ne briše da bi se posle
          znalo po čemu je koji račun izdat.
        </div>

        <div style={naglaseno}>
          <strong>Uz osnovu idu i ne naplaćuju se:</strong> predračuni, avansi, izveštaji, offline rad i
          više lokacija. Predračun i avans su vrste fiskalnog računa koje po propisu izdaje svako ko ima
          kasu; izveštaji i offline su deo svakog paketa; više objekata se naplaćuje <strong>po objektu</strong>
          (svaki sledeći 50 % cene), ne kao modul. Zatečene licence za njih ništa ne naplaćuju.
        </div>

        <div style={naglaseno}>
          <strong>Paket je cenovnik, modul je licenca (ADR-049).</strong> Klijent na paketu ima običan red
          licence po modulu sa istim <code>package_code</code>; cena paketa stoji na jednom redu, ostali nose 0,
          a na računu je paket <strong>jedna stavka</strong>. Cena ide u knjigu <code>package_prices</code> -
          nov red po promeni, sa datumom i autorom; iznosi su <strong>sa PDV-om</strong>. Nadogradnja radi
          odmah (uz kredit za neiskorišćeni deo), sniženje od sledećeg dana naplate. Dodatak uz paket se
          prodaje samo dok je zbir jeftiniji od sledećeg paketa - inače se nudi paket.
        </div>
      </Uputstvo>

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Šifra</th><th style={th}>Naziv</th><th style={th}>Moduli</th>
              <th style={{ ...th, textAlign: 'right' }} title="Važeća cena iz knjige package_prices, sa PDV-om">Cena / mes</th>
              <th style={{ ...th, textAlign: 'center' }} title="Prodaje se na sajtu i u samouslužnom katalogu">Javni</th>
              <th style={{ ...th, textAlign: 'right' }} title="Redosled u cenovniku - od najjeftinijeg">Red.</th>
              <th style={{ ...th, textAlign: 'right' }} title="Broj uređaja koji sme istovremeno da bude prijavljen; prepisuje se u licencu pri dodeli">Podraz. mesta</th>
              <th style={{ ...th, textAlign: 'right' }} title="Koliko dana licenca važi od dodele">Trajanje</th>
              <th style={{ ...th, textAlign: 'center' }} title="Paket na probu">Proba</th>
              <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
            </tr></thead>
            <tbody>
              {packages.length === 0 && (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Package size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema paketa</div>
                </td></tr>
              )}
              {packages.map((p) => (
                <tr key={p.id}>
                  <td style={{ ...td, fontFamily: 'monospace', color: 'var(--color-text-strong)', fontWeight: 600 }}>{p.code}</td>
                  <td style={{ ...td, color: 'var(--color-text-strong)' }}>{p.name}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {p.modules.map((m) => (
                        <span key={m} style={moduleBadge}>{m}</span>
                      ))}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.monthlyPriceRsd ? 'var(--color-text-strong)' : 'var(--color-text-muted)' }}>
                    {p.monthlyPriceRsd ? `${Number(p.monthlyPriceRsd).toLocaleString('sr-RS')} RSD` : '- bez cene'}
                    {p.yearlyPriceRsd && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{Number(p.yearlyPriceRsd).toLocaleString('sr-RS')} / god</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{p.isPublic ? '✓' : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.sortOrder}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.defaultSeats}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.defaultValidityDays}d</td>
                  <td style={{ ...td, textAlign: 'center' }}>{p.isTrial ? '✓' : ''}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setEditing(p)} style={iconBtn}><Edit size={13} /></button>
                    <button onClick={() => setDeleteTarget(p)} style={{ ...iconBtn, color: '#fca5a5' }}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <PackageFormDialog
          pkg={editing}
          allModules={modules}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši license paket"
          message={`Da li ste sigurni da želite da obrišete paket "${deleteTarget.name}" (${deleteTarget.code})? Postojeći tenant licence kreirane iz ovog paketa neće biti obrisane.`}
          confirmLabel="Obriši paket"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function PackageFormDialog({ pkg, allModules, onClose, onSaved }: { pkg: LicensePackage | null; allModules: LicenseModule[]; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const isEdit = !!pkg;
  const [code, setCode] = useState(pkg?.code || '');
  const [name, setName] = useState(pkg?.name || '');
  const [description, setDescription] = useState(pkg?.description || '');
  const [defaultSeats, setDefaultSeats] = useState(pkg?.defaultSeats || 1);
  const [defaultValidityDays, setDefaultValidityDays] = useState(pkg?.defaultValidityDays || 365);
  const [isTrial, setIsTrial] = useState(pkg?.isTrial || false);
  const [isPublic, setIsPublic] = useState(pkg?.isPublic || false);
  const [sortOrder, setSortOrder] = useState(pkg?.sortOrder ?? 0);
  // Cena se šalje samo ako je promenjena - inače bi svako čuvanje pravilo nov red u knjizi.
  const [monthlyPrice, setMonthlyPrice] = useState(pkg?.monthlyPriceRsd ?? '');
  const [yearlyPrice, setYearlyPrice] = useState(pkg?.yearlyPriceRsd ?? '');
  const cenaPromenjena = monthlyPrice !== (pkg?.monthlyPriceRsd ?? '') || yearlyPrice !== (pkg?.yearlyPriceRsd ?? '');
  const [selectedModules, setSelectedModules] = useState<Set<ModuleCode>>(new Set(pkg?.modules || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (code: ModuleCode) => {
    const next = new Set(selectedModules);
    if (next.has(code)) next.delete(code); else next.add(code);
    setSelectedModules(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || selectedModules.size === 0) {
      setError('Code, naziv i bar 1 modul su obavezni');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description,
        defaultSeats,
        defaultValidityDays,
        isTrial,
        isPublic,
        sortOrder,
        modules: Array.from(selectedModules),
        ...(cenaPromenjena && monthlyPrice !== '' ? { monthlyPriceRsd: monthlyPrice, yearlyPriceRsd: yearlyPrice || null } : {}),
      };
      if (isEdit) await api.put(`/admin/license-packages/${pkg.id}`, body);
      else await api.post('/admin/license-packages', body);
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>{isEdit ? `Izmena: ${pkg?.code}` : 'Novi paket'}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
            <Field label="Code *">
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} disabled={isEdit} placeholder="POS_BASIC" required style={input} />
            </Field>
            <Field label="Naziv *">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required style={input} />
            </Field>
          </div>
          <Field label="Opis">
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <Field label="Podrazumevana mesta">
              <input type="number" min={1} value={defaultSeats} onChange={(e) => setDefaultSeats(Number(e.target.value))} style={input} />
              <Napomena>Uređaja istovremeno. Prepisuje se u licencu pri dodeli; kasnija izmena ne dira zatečene klijente.</Napomena>
            </Field>
            <Field label="Trajanje (dani)">
              <input type="number" min={1} value={defaultValidityDays} onChange={(e) => setDefaultValidityDays(Number(e.target.value))} style={input} />
              <Napomena>Od dana dodele. Posle isteka ide još 7 dana grejsa.</Napomena>
            </Field>
            <Field label="Proba">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0' }}>
                <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Paket na probu</span>
              </label>
              <Napomena>Proba se sama ne pretvara u plaćenu.</Napomena>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
            <Field label="Cena / mes (RSD, sa PDV)">
              <input type="number" min={0} step="0.01" value={monthlyPrice} onChange={(e) => setMonthlyPrice(e.target.value)} placeholder="npr. 990" style={input} />
              <Napomena>Nov red u knjizi cena, važi od danas. Prazno = paket nema cenu i ne prodaje se.</Napomena>
            </Field>
            <Field label="Cena / god (RSD)">
              <input type="number" min={0} step="0.01" value={yearlyPrice} onChange={(e) => setYearlyPrice(e.target.value)} placeholder="10 × mesečna" style={input} />
              <Napomena>Prazno = deset mesečnih.</Napomena>
            </Field>
            <Field label="Redosled">
              <input type="number" min={0} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} style={input} />
              <Napomena>Od najjeftinijeg; određuje „sledeći paket" za upsell.</Napomena>
            </Field>
            <Field label="Javni">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0' }}>
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Prodaje se</span>
              </label>
              <Napomena>Sajt i samouslužni katalog vide samo javne pakete sa cenom.</Napomena>
            </Field>
          </div>
          <Field label="Moduli uključeni u paket *">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 10, background: 'var(--color-bg)', borderRadius: 6, border: '1px solid var(--color-border)' }}>
              {allModules.map((m) => (
                <label key={m.code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedModules.has(m.code)} onChange={() => toggle(m.code)} />
                  <span style={{ fontSize: 12, color: 'var(--color-text)' }}>{m.name} <span style={{ opacity: 0.5, fontFamily: 'monospace' }}>({m.code})</span></span>
                </label>
              ))}
            </div>
          </Field>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const moduleBadge: React.CSSProperties = { fontSize: 10, padding: '2px 6px', borderRadius: 10, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontFamily: 'monospace', fontWeight: 600 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
/** Rečenica ispod polja - pravilo koje se iz samog polja ne vidi. */
function Napomena({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.5, color: 'var(--color-text-muted)' }}>
      {children}
    </div>
  );
}

const naglaseno: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, background: 'var(--color-bg)',
  color: 'var(--color-text-muted)', lineHeight: 1.6,
};

const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 560, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)', maxHeight: '90vh', overflow: 'auto' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg-surface)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
